import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ServiceLayout from "@/components/ServiceLayout";
import PhaseHero from "@/components/how-it-works/PhaseHero";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

const heroImage =
  "https://bepnmmikbflhezryppcs.supabase.co/storage/v1/object/public/pictures1/services-heroes/Graphics-24.png";

const instruments = [
  {
    title: "Debt Instruments",
    description:
      "Turn machines into investable debt products. Industrial robots and equipment are financed through secured bonds and leasing notes, backed by machine value and usage contracts. Investors gain predictable returns with exposure to real-world assets.",
  },
  {
    title: "Equity and Asset Finance Instruments",
    description:
      "Invest in a diversified pool of industrial machines through SPV equity participation and structured asset finance, including sale-and-leaseback and leasing arrangements, generating operational income and potential capital appreciation.",
  },
  {
    title: "Asset-Backed Securities",
    description:
      "Bundle multiple machines and service contracts into structured products. Machine-backed ABS diversify risk and provide investors with performance-linked returns, secured by both receivables and residual machine value.",
  },
  {
    title: "Receivables-Backed Notes",
    description:
      "Transform service contracts into tradeable instruments. These represent machine-generated invoices, giving investors access to short-term, receivables-backed yields.",
  },
];

const rwafiLayers = [
  {
    number: "01",
    title: "Asset layer",
    description:
      "Identifies and securely custodies the physical asset — often through Special Purpose Vehicles (SPVs) with compliance oracles attesting to what exists and who owns it.",
  },
  {
    number: "02",
    title: "Protocol layer",
    description:
      "Converts asset ownership into blockchain tokens using standards such as ERC-1400, embedding transfer rules and compliance directly into the token.",
  },
  {
    number: "03",
    title: "Application layer",
    description:
      "Connects tokenized assets to DeFi lending, staking, and liquidity pools so real-world collateral becomes productive on-chain.",
  },
];

const benefits = [
  {
    title: "Sustainable yield",
    description:
      "Replaces volatile crypto-farming rewards with predictable, asset-backed returns.",
  },
  {
    title: "Capital efficiency",
    description:
      "Unlocks liquidity from traditionally illiquid assets such as industrial equipment, real estate, or private credit.",
  },
  {
    title: "Institutional bridge",
    description:
      "Provides compliant, regulated entry points for major financial players like BlackRock and WisdomTree.",
  },
];

const challenges = [
  {
    title: "Regulatory friction",
    description:
      "Navigating complex, cross-jurisdictional compliance and securities laws.",
  },
  {
    title: "Liquidity fragmentation",
    description:
      "Tokenized capital spread across disparate blockchain networks with uneven depth.",
  },
  {
    title: "Legal complexity",
    description:
      "Resolving conflicts between smart-contract execution and traditional bankruptcy law.",
  },
];

const ecosystems = [
  {
    logo: "/ecosystems/plume-network.jpeg",
    logoAlt: "Plume Network logo",
    name: "Plume Network",
    description:
      "A modular L1/L2 blockchain infrastructure custom-built for RWA tokenization and financialization.",
  },
  {
    logo: "/ecosystems/centrifuge.jpeg",
    logoAlt: "Centrifuge logo",
    name: "Centrifuge",
    description:
      "A prominent protocol bridging real-world credit and off-chain assets to DeFi.",
  },
  {
    logo: "/ecosystems/peaq.jpeg",
    logoAlt: "peaq network logo",
    name: "peaq network",
    description:
      "Purpose-built infrastructure for the machine economy, where devices and equipment transact autonomously.",
  },
  {
    logo: "/ecosystems/iotex.png",
    logoAlt: "IoTeX logo",
    name: "IoTeX",
    description:
      "Connects AI and machine data to verifiable on-chain proofs, enabling machine-generated value to be financed.",
  },
];

const financingProcess = [
  {
    number: "01",
    title: "Asset Selection",
    actions: [
      "Qualify your asset",
      "Appraise asset value",
      "Provide financial and property details",
    ],
  },
  {
    number: "02",
    title: "Legal Structure",
    actions: [
      "Define token rights",
      "Select jurisdictions",
      "Create a Special Purpose Vehicle (SPV)",
    ],
  },
  {
    number: "03",
    title: "Technology & Integrations",
    actions: [
      "Configure token specifications",
      "Select KYC/AML, custody, and payment solutions",
      "Deploy the platform",
    ],
  },
  {
    number: "04",
    title: "Select Distribution",
    actions: [
      "Launch the token offering",
      "Manage investor relations",
      "Invest and manage",
    ],
  },
];

const AssetFinancing = () => {
  useDocumentTitle(
    "Asset Financing — Ways to access liquidity | Klexu",
    "Turn machines, receivables, inventory, and future revenues into flexible financing: debt, equity, asset-backed securities, and RWAfi structures."
  );

  return (
    <>
      <Navbar />
      <PhaseHero
        number="03"
        eyebrow="Asset Financing"
        titleLead="Ways to access"
        titleHighlight="liquidity."
        body="From machines to receivables, inventory and future revenues, our platform transforms real manufacturing assets into flexible financing opportunities."
        whatYouGet="A selected financing structure — debt, equity, asset-backed securities, or receivables notes — matched to your assets and contracts."
        whoItsFor="Industrial operators, asset owners, and investors seeking capital-efficient, performance-linked equipment funding."
        ctaLabel="Explore Structures"
        image={heroImage}
      />

      <ServiceLayout
        title="Ways to access liquidity"
        description=""
        icon={null}
      >
        {/* Instruments */}
        <div className="mb-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/20">
          <span className="text-xs font-semibold tracking-[0.2em] text-accent uppercase">
            PoC
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {instruments.map((item) => (
            <div
              key={item.title}
              className="glass-card rounded-xl p-6 border border-border/60 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
            >
              <h3 className="mb-3 text-lg font-bold text-foreground">{item.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {item.description}
              </p>
            </div>
          ))}
        </div>

        <Separator className="my-12 bg-white/10" />

        {/* RWAfi */}
        <div className="space-y-6">
          <h3 className="text-2xl md:text-3xl font-bold text-foreground">
            Selecting the right structure with RWAfi
          </h3>
          <p className="text-muted-foreground leading-relaxed max-w-3xl">
            RWAfi (Real World Asset Finance) tokenizes traditional physical and
            financial assets — such as real estate, U.S. Treasuries, private credit,
            and commodities — and integrates them directly into decentralized finance
            (DeFi). For industrial businesses, the same mechanics apply to machines: a
            verified outcome stream from an asset can become a financeable,
            programmable unit of value.
          </p>

          <h4 className="text-lg font-semibold text-foreground pt-2">
            How RWAfi works
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {rwafiLayers.map((layer) => (
              <div
                key={layer.number}
                className="glass-card rounded-xl p-6 border border-border/60 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <span className="text-3xl font-bold text-accent/70">
                  {layer.number}
                </span>
                <h5 className="text-lg font-bold text-foreground mt-3 mb-2">
                  {layer.title}
                </h5>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {layer.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <Separator className="my-12 bg-white/10" />

        {/* Benefits & challenges */}
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <h4 className="text-lg font-semibold mb-5 text-foreground">
              Core benefits
            </h4>
            <div className="space-y-4">
              {benefits.map((b) => (
                <div key={b.title} className="flex items-start gap-3">
                  <div>
                    <p className="font-semibold text-foreground">{b.title}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {b.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-lg font-semibold mb-5 text-muted-foreground">
              Key challenges
            </h4>
            <div className="space-y-4">
              {challenges.map((c) => (
                <div key={c.title} className="flex items-start gap-3">
                  <div>
                    <p className="font-semibold text-foreground">{c.title}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {c.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Separator className="my-12 bg-white/10" />

        {/* How it works */}
        <section className="space-y-7" aria-labelledby="financing-process-title">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-accent">
                From asset to market
              </p>
              <h4 id="financing-process-title" className="text-2xl font-bold text-foreground md:text-3xl">
                How It Works
              </h4>
            </div>
            <p className="max-w-md text-sm leading-relaxed text-muted-foreground sm:text-right">
              Structure, configure, and distribute an investable industrial asset through one connected process.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
            {financingProcess.map((step, index) => (
              <div key={step.number} className="relative flex">
                <article className="glass-card flex min-h-[270px] w-full flex-col rounded-lg border border-border/60 p-5 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
                  <div className="mb-6 flex items-center justify-between">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground">
                      {step.number}
                    </span>
                    {index < financingProcess.length - 1 && <span className="hidden text-lg text-accent/60 lg:block" aria-hidden="true">→</span>}
                  </div>
                  <h5 className="text-lg font-bold text-foreground">{step.title}</h5>
                  <ul className="mt-4 space-y-3">
                    {step.actions.map((action) => (
                      <li key={action} className="flex items-start gap-2 text-sm leading-relaxed text-muted-foreground">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />
                        {action}
                      </li>
                    ))}
                  </ul>
                </article>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-4 rounded-lg border border-accent/20 bg-accent/5 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-semibold text-foreground">Invest &amp; manage</p>
              <p className="mt-1 text-sm text-muted-foreground">Move from structured asset to active investment and ongoing management.</p>
            </div>
            <Button asChild size="lg" className="shrink-0">
              <Link to="/consultation">
                Get started
              </Link>
            </Button>
          </div>
        </section>

        <Separator className="my-12 bg-white/10" />

        {/* Ecosystems */}
        <div className="space-y-6">
          <h4 className="text-lg font-semibold text-foreground">
            Leading ecosystems
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ecosystems.map((e) => (
              <div
                key={e.name}
                className="glass-card rounded-xl p-6 border border-border/60 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="mb-4 flex items-center gap-4">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-card p-2">
                    <img src={e.logo} alt={e.logoAlt} className="h-full w-full object-contain" />
                  </div>
                  <p className="font-bold text-foreground">{e.name}</p>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {e.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 rounded-xl border border-accent/20 bg-accent/5 px-6 py-8 text-center">
          <p className="text-lg md:text-xl font-semibold text-foreground mb-4">
            Not sure which structure fits your assets?
          </p>
          <Link to="/consultation">
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
              Book a Consultation
            </Button>
          </Link>
        </div>
      </ServiceLayout>
      <Footer />
    </>
  );
};

export default AssetFinancing;
